import type { ReactNode } from 'react';
import { CLOSING_DOCUMENT_SHADOW } from './closing-styles';

/**
 * Paper graphic used for a closing document before and after the file exists.
 * The page shadow stays in place once the real document is rendered inside it.
 */
export default function ClosingDocumentSheet({
  children,
  filled = false,
  overlay,
  footer,
  intrinsic = false,
  plain = false,
  footerPlacement = 'bottom',
}: {
  children: ReactNode;
  filled?: boolean;
  overlay?: ReactNode;
  /** Sits on the page, above a review stamp, so it stays clickable. */
  footer?: ReactNode;
  /** Let an image or the first PDF page set the page size. */
  intrinsic?: boolean;
  /** Page with no placeholder lines. Used when the sheet is not a file. */
  plain?: boolean;
  footerPlacement?: 'bottom' | 'center';
}) {
  const sizedByFile = filled && intrinsic;
  const widthClass = sizedByFile ? 'max-w-[16rem]' : 'max-w-[13.5rem]';
  const frameClass = sizedByFile ? '' : 'aspect-[3/4]';
  return (
    <div className={`w-full min-w-0 rounded-md ${CLOSING_DOCUMENT_SHADOW} ${widthClass}`}>
      <div className={`relative overflow-hidden rounded-md border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900 ${frameClass}`}>
        {sizedByFile ? children : null}
        {filled && !intrinsic ? <div className="absolute inset-0">{children}</div> : null}
        {filled || plain ? null : (
          <>
            <div aria-hidden className="pointer-events-none absolute inset-x-4 top-4 space-y-1.5">
              <div className="h-1.5 w-1/2 rounded-full bg-gray-200 dark:bg-white/15" />
              <div className="h-1 w-full rounded-full bg-gray-100 dark:bg-white/10" />
              <div className="h-1 w-5/6 rounded-full bg-gray-100 dark:bg-white/10" />
              <div className="h-1 w-full rounded-full bg-gray-100 dark:bg-white/10" />
            </div>
            <div aria-hidden className="pointer-events-none absolute inset-x-4 bottom-4 space-y-1.5">
              <div className="h-1 w-full rounded-full bg-gray-100 dark:bg-white/10" />
              <div className="h-1 w-4/5 rounded-full bg-gray-100 dark:bg-white/10" />
              <div className="h-1 w-2/3 rounded-full bg-gray-100 dark:bg-white/10" />
            </div>
            <p className="absolute inset-0 flex items-center justify-center px-5 text-center text-xs leading-4 text-gray-500 dark:text-gray-400">
              {children}
            </p>
          </>
        )}
        <span
          aria-hidden
            className="pointer-events-none absolute right-0 top-0 z-10 h-8 w-8 bg-gray-100 shadow-[-8px_10px_16px_-6px_rgba(15,23,42,0.55)] dark:bg-white/10"
          style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
        />
        {overlay}
        {footer ? (
          <div
            className={
              footerPlacement === 'center'
                ? 'absolute inset-0 z-30 flex items-center justify-center px-4'
                : 'absolute inset-x-0 bottom-0 z-30 px-3 pb-3'
            }
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
